import type { UsageSummaryPage, UsageDetailsPage, UsageAnomalySummaryPage } from "./admin-usage";
import { buildPublicError, type PublicErrorLocale } from "./errors";

export const USAGE_QUERY_BASE_PATH = "/v1/usage";
export const USAGE_QUERY_MAX_WINDOW_DAYS = 31;
export const USAGE_QUERY_DEFAULT_LIMIT = 100;
export const USAGE_QUERY_ENDPOINTS = {
  summary: { path: "/summary", maxLimit: 500 },
  details: { path: "/details", maxLimit: 200 },
  anomalies: { path: "/anomalies/summary" },
} as const;
export const USAGE_QUERY_LIST_FILTERS = [
  "userIds", "apiKeyIds", "providerIds", "channelIds", "modelIds", "operations",
  "requestStatuses", "usageSources", "normalizationStatuses", "ratingStatuses",
] as const;
export const USAGE_QUERY_UUID_FILTERS = ["apiKeyIds", "providerIds", "channelIds", "modelIds"] as const;
export const USAGE_QUERY_PARAMETERS = [
  "start", "end", "timeZoneOffsetMinutes", "groupBy", "limit", "cursor",
  ...USAGE_QUERY_LIST_FILTERS, "upstreamModelId", "breakdown",
] as const;
export const USAGE_QUERY_RESPONSE_FIELDS = [
  "group", "requests", "usage", "rating", "tokens", "metrics", "amounts", "costReporting", "nextCursor", "details", "categories", "breakdowns",
] as const;
export const USAGE_QUERY_ERROR_STATUSES = [
  { status: 400, code: "invalid_usage_query" },
  { status: 401, code: "authentication_required" },
  { status: 403, code: "admin_required" },
  { status: 500, code: "internal_error" },
] as const;

export function usageQueryCurlExample(endpoint = `${USAGE_QUERY_BASE_PATH}${USAGE_QUERY_ENDPOINTS.summary.path}`): string {
  return [
    `curl --get "${endpoint.startsWith("/") ? "$NEXRA_BASE_URL" : ""}${endpoint}" \\`,
    '  --header "Authorization: Bearer $NEXRA_API_KEY" \\',
    '  --data-urlencode "start=2026-10-01T00:00:00Z" \\',
    '  --data-urlencode "end=2026-10-02T00:00:00Z" \\',
    '  --data-urlencode "groupBy=hour,model" \\',
    '  --data-urlencode "limit=100"',
  ].join("\n");
}

/** Illustrative data only; it never reads real accounts, keys or usage. */
export function usageQueryReferenceExamples(locale: PublicErrorLocale = "en") {
  const modelId = "11111111-1111-4111-8111-111111111111";
  return {
    summary: {
      data: [{
        group: { time: "2026-10-01T00:00:00.000Z", modelId },
        requests: { total: 2, succeeded: 2, failed: 0 },
        usage: { evidence: 1, missing: 1, sources: { provider: 1, estimated: 0, reconciled: 0 }, normalization: { normalized: 1, partial: 0, invalid: 0 } },
        rating: { billed: 1, unsettled: 0, billing_failed: 0, unpriced: 0, usage_missing: 1, usage_not_expected: 0 },
        costReporting: { reported: 0, missingCost: 1, missingRate: 0 },
        tokens: { applicable: null, requestsWithTokenMetrics: 1 },
        metrics: { input_tokens: 0, output_tokens: 12 },
        amounts: [{ kind: "sale", currency: "CNY", amount: "0.12345678" }],
      }], nextCursor: null,
    } satisfies UsageSummaryPage,
    details: {
      data: [{
        requestId: "22222222-2222-4222-8222-222222222222", requestKey: "request-example",
        receivedAt: "2026-10-01T00:00:00.000Z", status: "succeeded", httpStatus: null, durationMs: null, errorCode: null,
        userId: "user-example", userEmail: "user@example.invalid", apiKeyId: null, modelId, modelPublicId: "example/model",
        inputMode: null, inputFeatures: [], requestedModel: "example/model", operation: "language.generate", publicProtocol: "openai-chat-completions",
        providerId: null, channelId: null, upstreamModelId: null,
        usage: { id: null, source: null, occurredAt: null, rawUsage: {}, normalizedUsage: {}, normalizationStatus: "missing" },
        ratingStatus: "usage_missing", charge: null,
      }], nextCursor: null,
    } satisfies UsageDetailsPage,
    anomalies: {
      categories: [{ category: "usage_missing", count: 1, firstOccurredAt: "2026-10-01T00:00:00.000Z", lastOccurredAt: "2026-10-01T00:00:00.000Z" }],
      breakdowns: [{ category: "usage_missing", dimension: "model", dimensionId: modelId, count: 1 }],
    } satisfies UsageAnomalySummaryPage,
    error: buildPublicError({ code: "invalid_usage_query" }, locale, "nexra-query-example", 400),
  };
}
